import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Trash, MagnifyingGlass, Funnel, Star, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useConfirmDialog } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter19 Task 10 — Admin → Abonentët tab.
 * Lists all ACTIVE subscriptions (cash + app + walk-in). Filters by plan + free-text
 * search on plate or customer name. Cancel = real backend DELETE-via-cancel with a
 * styled confirmation. Refresh is optimistic so the row disappears without a reload.
 */
export default function AdminSubscribersTab() {
  const { t, fmtDate } = useLanguage();
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const { ConfirmDialog, confirm } = useConfirmDialog();
  // Iter21 Task 8 — review request modal state.
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions?status=active&limit=500`, { withCredentials: true }),
        axios.get(`${API}/subscription-plans`).catch(() => ({ data: [] })),
      ]);
      setItems(subsRes.data || []);
      setPlans(plansRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (planFilter !== 'all' && s.plan_id !== planFilter) return false;
      if (!q) return true;
      const plate = (s.license_plate || '').toLowerCase();
      const name = (s.customer_name || '').toLowerCase();
      return plate.includes(q) || name.includes(q);
    });
  }, [items, search, planFilter]);

  const cancelOne = async (s) => {
    const ok = await confirm({
      title: t('cancelSubConfirmTitle'),
      description: `${s.plan_name}${s.license_plate ? ` · ${s.license_plate}` : ''} — ${t('cancelSubConfirmBody')}`,
      confirmText: t('cancelSubConfirmOk'),
      cancelText: t('back') || 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/subscriptions/${s.subscription_id}/cancel`, {}, { withCredentials: true });
      setItems((prev) => prev.filter((row) => row.subscription_id !== s.subscription_id));
      toast.success(t('autoSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const originLabel = (s) => {
    if (s.source === 'cash_manager' || s.source === 'cash_staff' || s.is_cash) return t('subscribersOriginCash');
    if (s.user_id && (s.customer_name || '').toLowerCase().startsWith('walk')) return t('subscribersOriginWalkin');
    return t('subscribersOriginApp');
  };

  const fmtDateLocal = (iso) => fmtDate(iso, { day: '2-digit', month: 'short', year: '2-digit' });

  // Iter21 Task 8 — request a Google review for this customer.
  const requestReview = async (s) => {
    if (!s.user_id) {
      toast.error(t('reviewModalNoPhone'));
      return;
    }
    setReviewBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/review-request`,
        { user_id: s.user_id }, { withCredentials: true });
      setReviewModal({ ...r.data, customer_name: s.customer_name_display || s.customer_name });
      // Optimistic state update — review_status flips to "requested".
      setItems((prev) => prev.map((x) => x.subscription_id === s.subscription_id
        ? { ...x, review_status: 'requested' } : x));
      toast.success(t('reviewRequestSent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setReviewBusy(false);
    }
  };

  const reviewStatusLabel = (status) => {
    if (status === 'requested') return t('reviewStatusRequested');
    if (status === 'completed') return t('reviewStatusCompleted');
    return t('reviewStatusNotRequested');
  };

  return (
    <div className="space-y-3" data-testid="admin-subscribers-tab">
      {/* Search + Plan filter */}
      <div className="grid grid-cols-[1fr_180px] gap-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('subscribersSearchPlaceholder')}
            className="h-10 pl-9 bg-[#0a0a0a] border-white/10 rounded-xl text-sm"
            data-testid="subscribers-search"
          />
        </div>
        <div className="relative">
          <Funnel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="h-10 w-full pl-9 pr-3 bg-[#0a0a0a] border border-white/10 rounded-xl text-sm text-white appearance-none"
            data-testid="subscribers-plan-filter"
          >
            <option value="all">{t('subscribersFilterAllPlans')}</option>
            {plans.map((p) => (
              <option key={p.plan_id} value={p.plan_id}>{p.name_en}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-[11px] text-white/45" data-testid="subscribers-count">
        {filtered.length} / {items.length}
      </p>

      {loading ? (
        <p className="text-sm text-white/45">…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-white/45 py-6 text-center" data-testid="subscribers-empty">
          {t('subscribersEmpty')}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => (
            <div
              key={s.subscription_id}
              className="p-3 rounded-2xl bg-[#121212] border border-white/5 flex items-center gap-3"
              data-testid={`subscriber-row-${s.subscription_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white truncate">
                    {s.customer_name_display || s.customer_name || 'Walk-in'}
                  </span>
                  {s.license_plate && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                      {s.license_plate}
                    </span>
                  )}
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#0ABAB5]/15 text-[#0ABAB5]">
                    {s.plan_name}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700]">
                    {originLabel(s)}
                  </span>
                  {/* Iter21 Task 8 — review status badge */}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.review_status === 'completed' ? 'bg-green-500/15 text-green-400' : s.review_status === 'requested' ? 'bg-white/10 text-white/70' : 'bg-white/5 text-white/40'}`}
                        data-testid={`subscriber-review-status-${s.subscription_id}`}>
                    {reviewStatusLabel(s.review_status)}
                  </span>
                </div>
                <p className="text-[11px] text-white/50 mt-1">
                  {t('subscribersWashesLeft')}: <strong>{s.washes_remaining ?? '∞'}</strong>
                  {' · '}{t('subscribersActivatedOn')}: {fmtDateLocal(s.created_at)}
                  {' · '}{t('subscribersExpiresOn')}: {fmtDateLocal(s.expires_at)}
                </p>
              </div>
              {/* Iter21 Task 8 — Review request button (only when not yet completed) */}
              {s.review_status !== 'completed' && (
                <button
                  onClick={() => requestReview(s)}
                  disabled={reviewBusy}
                  className="p-2 rounded-xl bg-[#FFD700]/10 hover:bg-[#FFD700]/20 text-[#FFD700] shrink-0 disabled:opacity-50"
                  data-testid={`subscriber-review-${s.subscription_id}`}
                  title={t('reviewRequest')}
                >
                  <Star size={16} weight="fill" />
                </button>
              )}
              <button
                onClick={() => cancelOne(s)}
                className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 shrink-0"
                data-testid={`subscriber-delete-${s.subscription_id}`}
                title={t('cancelSubConfirmOk')}
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog />

      {/* Iter21 Task 8 — Review request modal */}
      <Dialog open={!!reviewModal} onOpenChange={() => setReviewModal(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Star size={18} weight="fill" className="text-[#FFD700]" />
              {t('reviewModalTitle')}
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs">
              {t('reviewModalDesc')}
            </DialogDescription>
          </DialogHeader>
          {reviewModal && (
            <div className="space-y-3 pt-1">
              <div className="p-3 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/30">
                <p className="text-[10px] uppercase tracking-wider text-[#FFD700]/80 mb-1">{t('reviewModalCode')}</p>
                <p className="font-mono text-lg font-bold text-[#FFD700]" data-testid="review-modal-code">{reviewModal.code}</p>
                <p className="text-xs text-[#FFD700]/70 mt-1">{t('reviewModalDiscount')}: {reviewModal.discount_pct}%</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/70 whitespace-pre-line" data-testid="review-modal-message">
                {reviewModal.message}
              </div>
              {reviewModal.whatsapp_url ? (
                <Button
                  onClick={() => { window.open(reviewModal.whatsapp_url, '_blank'); setReviewModal(null); }}
                  className="w-full h-11 bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold rounded-xl"
                  data-testid="review-modal-whatsapp"
                >
                  <WhatsappLogo size={16} weight="fill" className="mr-2" />
                  {t('reviewOpenWhatsApp')}
                </Button>
              ) : (
                <p className="text-xs text-white/50 text-center">{t('reviewModalNoPhone')}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
